import * as React from 'react';
import { useLocation, useNavigate } from 'react-router';

import { useApolloClient } from '@apollo/client';

import { DrawerContext } from '@notino/react-styleguide';
import {
  GetShadefinderQueryQuery,
  GetShadefinderQueryQueryVariables,
} from '@notino/shared/definitions/types';

import { MultistepModal } from '@components/MultistepModal';
import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetail/ProductDetailContext';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { ProductEventWither } from '@helpers/googleTagManager';
import { useEffectOnlyFirstMount } from '@hooks/useEffectOnlyFirstMount';
import { resolveDrawerCloseType } from '@utils/drawer';
import { isDesktop } from '@utils/helpers';

import { ShadefinderStep1 } from '../components/steps/Step1';
import { DRAWER_SHADEFINDER_CONFIG, SHADEFINDER_CONFIG } from '../config';
import getShadefinder from '../queries/getShadefinderQuery.graphql';
import {
  trackShadefinderOpen,
  trackShadefinderClose,
  trackShadefinderBack,
} from '../tracking';

export type IUseShadefinder = () => void;

export const useShadefinder = (masterId?: string): IUseShadefinder => {
  const client = useApolloClient();
  const {
    openModal,
    closeModal,
    back: backModal,
  } = MultistepModal.useMultistepModal();
  const {
    openDrawer,
    closeDrawer,
    back: backDrawer,
  } = DrawerContext.useDrawerContext();
  const { getVariant, getTimeFromInit } = useTrackingContext();
  const { product, currentVariant } = useProductDetailContext();
  const navigate = useNavigate();
  const { hash } = useLocation();

  const openShadeFinder = React.useCallback(() => {
    function prefetchData() {
      const options = {
        query: getShadefinder,
        variables: {
          productId: masterId,
        },
      };
      const isAlreadyCached = client.cache.readQuery(options);

      if (!isAlreadyCached) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        client.query<
          GetShadefinderQueryQuery,
          GetShadefinderQueryQueryVariables
        >(options);
      }
    }

    prefetchData();
    trackShadefinderOpen({
      product: ProductEventWither()
        .withProduct(product)
        .withVariant(currentVariant)
        .build(),
      timing: getTimeFromInit(),
      variant: getVariant(),
    });

    const commonProps = {
      currentStep: 1,
      totalSteps: 4,
      name: 'select_shade',
    };

    const timeOfStart = new Date().getTime();

    if (isDesktop()) {
      openModal(
        <ShadefinderStep1 masterId={masterId} timeOfStart={timeOfStart} />,
        {
          ...commonProps,
          onClose: (name) => {
            trackShadefinderClose({
              variant: getVariant(),
              timing: getTimeFromInit(timeOfStart),
              name,
            });
            closeModal();
            navigate({ pathname: window.location.pathname });
          },
          modalProps: {
            background: SHADEFINDER_CONFIG.background,
          },
          onBack: (name) => {
            trackShadefinderBack({
              variant: getVariant(),
              timing: getTimeFromInit(timeOfStart),
              name,
            });
            backModal();
          },
        }
      );
    } else {
      openDrawer(
        <ShadefinderStep1 masterId={masterId} timeOfStart={timeOfStart} />,
        {
          onClose: (closeType, name) => {
            trackShadefinderClose({
              variant: getVariant(),
              timing: getTimeFromInit(timeOfStart),
              interaction: resolveDrawerCloseType(closeType),
              name,
            });
            closeDrawer();
            navigate({ pathname: window.location.pathname });
          },
          onBack: (name) => {
            trackShadefinderBack({
              variant: getVariant(),
              timing: getTimeFromInit(timeOfStart),
              name,
            });
            backDrawer();
          },
          ...commonProps,
          ...DRAWER_SHADEFINDER_CONFIG,
        }
      );
    }
  }, [
    backDrawer,
    backModal,
    client,
    closeDrawer,
    closeModal,
    currentVariant,
    getTimeFromInit,
    getVariant,
    masterId,
    navigate,
    openDrawer,
    openModal,
    product,
  ]);

  useEffectOnlyFirstMount(() => {
    if (hash === '#shadefinder') {
      const shadefinderTimeout = setTimeout(() => openShadeFinder(), 500);
      return () => clearTimeout(shadefinderTimeout);
    }
  }, [hash, openShadeFinder]);

  return () => {
    navigate({ pathname: window.location.pathname, hash: 'shadefinder' });
    openShadeFinder();
  };
};
